import { useCallback, useEffect, useState } from "react";

import { apiFetch } from "@/lib/api-client";
import { formatDateTime } from "@/lib/i18n";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface FunnelStage {
  id: string;
  label: string;
  count: number | null;
  conversionRate: number | null;
  available: boolean;
  source: string;
}

interface TrialFunnelResponse {
  generatedAt: string;
  windowDays: number;
  analyticsSource: string;
  stages: FunnelStage[];
}

function TrialFunnelSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-8 w-56" />
        <Skeleton className="mt-2 h-4 w-80" />
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <SkeletonCard key={index} className="h-24" />
        ))}
      </div>
      <Skeleton className="h-72 w-full rounded-lg" />
    </div>
  );
}

function formatPercent(value: number | null) {
  if (value === null) {
    return "Unavailable";
  }

  return `${value.toFixed(1)}%`;
}

export default function TrialFunnelPage() {
  const [funnel, setFunnel] = useState<TrialFunnelResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadFunnel = useCallback(async () => {
    try {
      const response = await apiFetch<{ data: TrialFunnelResponse }>("/api/admin/operations/trial-funnel");
      setFunnel(response.data);
      setError(null);
    } catch {
      setFunnel(null);
      setError("Trial funnel analytics are currently unavailable.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFunnel();
  }, [loadFunnel]);

  if (isLoading) {
    return <TrialFunnelSkeleton />;
  }

  const maxStageCount = Math.max(1, ...(funnel?.stages.map((stage) => stage.count ?? 0) ?? [1]));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-foreground)]">Trial Funnel</h1>
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
          Cross-tenant activation funnel for the last {funnel?.windowDays ?? 90} days.
          {funnel ? ` Generated ${formatDateTime(funnel.generatedAt)}.` : ""}
        </p>
      </div>

      {error ? (
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <h2 className="text-base font-semibold text-[var(--color-foreground)]">Trial funnel unavailable</h2>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">{error}</p>
          <button
            onClick={() => {
              setIsLoading(true);
              void loadFunnel();
            }}
            className="mt-4 rounded-md border border-[var(--color-border)] px-4 py-2 text-sm"
          >
            Retry
          </button>
        </div>
      ) : null}

      {funnel ? (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
              <p className="text-xs text-[var(--color-muted-foreground)]">Analytics source</p>
              <p className="mt-1 text-2xl font-bold text-[var(--color-foreground)]">{funnel.analyticsSource}</p>
              <p className="text-xs text-[var(--color-muted-foreground)]">ClickHouse backs event stages when available; Postgres anchors signups and paid conversion.</p>
            </div>
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
              <p className="text-xs text-[var(--color-muted-foreground)]">Top stage</p>
              <p className="mt-1 text-2xl font-bold text-[var(--color-foreground)]">{funnel.stages[0]?.count ?? "—"}</p>
              <p className="text-xs text-[var(--color-muted-foreground)]">{funnel.stages[0]?.label ?? "Signups"}</p>
            </div>
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
              <p className="text-xs text-[var(--color-muted-foreground)]">Paid conversion</p>
              <p className="mt-1 text-2xl font-bold text-[var(--color-success)]">{formatPercent(funnel.stages.at(-1)?.conversionRate ?? null)}</p>
              <p className="text-xs text-[var(--color-muted-foreground)]">Final stage against signups in the same window.</p>
            </div>
          </div>

          <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Funnel stages</h2>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                Stage counts are distinct tenants. Missing middle stages stay explicitly unavailable during Postgres fallback instead of showing fake zeros.
              </p>
            </div>
            <div className="space-y-4">
              {funnel.stages.map((stage) => {
                const numericCount = stage.count ?? 0;
                const width = stage.available ? `${Math.max(8, Math.round((numericCount / maxStageCount) * 100))}%` : "12%";

                return (
                  <div key={stage.id} className="stagger-item rounded-lg border border-[var(--color-border)] p-4">
                    <div className="mb-2 flex items-center justify-between gap-4">
                      <div>
                        <h3 className="font-medium text-[var(--color-foreground)]">{stage.label}</h3>
                        <p className="text-xs text-[var(--color-muted-foreground)]">{stage.source}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-[var(--color-foreground)]">
                          {stage.count ?? "—"}
                        </p>
                        <p className="text-xs text-[var(--color-muted-foreground)]">{formatPercent(stage.conversionRate)}</p>
                      </div>
                    </div>
                    <div className="h-3 rounded-full bg-[var(--color-border)]">
                      <div
                        className={`h-full rounded-full transition-all ${stage.available ? "bg-[var(--color-primary)]" : "bg-[var(--color-warning)]/60"}`}
                        style={{ width }}
                      />
                    </div>
                    {!stage.available ? (
                      <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                        This stage is event-backed and stays unavailable when ClickHouse is not mounted.
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>

          <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Boundary</h2>
            <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
              The older win-back campaign controls are intentionally not shown here because that runtime is not mounted on the current admin seam. This page is now only the real trial funnel contract.
            </p>
          </section>
        </>
      ) : null}
    </div>
  );
}
